import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Skeleton, Tooltip } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { registerShortcut } from '@/lib/shortcuts';
import { trackEvent } from '@/lib/telemetry';
import { MerchantPickerDrawer } from './MerchantPickerDrawer';

// The merchant identity card at the top of the LEFT rail — the mirror of the
// patient card on provider web: the left rail is about the selected subject,
// and this says which one. Click (or ⌘M) opens the merchant picker drawer.
//
// Replaces the header merchant chip. ⌘M is registered HERE, by the component
// that is actually mounted; it used to live in MerchantCard, which nothing
// mounted, so the shortcut the chip advertised did nothing.

export const MerchantRailCard = observer(function MerchantRailCard({ wide, isDark }: { wide: boolean; isDark: boolean }) {
  const { merchantContextStore: ctx } = useStore();
  const [open, setOpen] = useState(false);
  const merchant = ctx.selectedMerchant;

  useEffect(
    () =>
      registerShortcut({
        key: 'm',
        meta: true,
        description: 'Switch merchant',
        handler: (e) => {
          e.preventDefault();
          setOpen(true);
          trackEvent('Merchant.PickerOpened', { source: 'cmd+m' });
        },
      }),
    []
  );

  // Session restored an id but the list is still loading → skeleton, never a
  // false "Pick a merchant" (the flash that read as a lost context).
  const showSkeleton = !merchant && ctx.selectedMerchantId != null && (ctx.isLoading || ctx.merchants.length === 0);
  const title = merchant?.title ?? (showSkeleton ? '' : 'Pick a merchant');
  const initials = merchant ? initialsOf(merchant.title) : '?';

  function openPicker(): void {
    setOpen(true);
    trackEvent('Merchant.PickerOpened', { source: 'rail-card' });
  }

  const avatar = (
    <span
      style={{
        width: 34,
        height: 34,
        borderRadius: 10,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        color: '#FFFFFF',
        fontSize: 12.5,
        fontWeight: 700,
        letterSpacing: '0.02em',
        background: merchant
          ? `linear-gradient(135deg, ${colors.ink[500]}, ${colors.ink[700]})`
          : isDark ? colors.slate[700] : colors.slate[300],
      }}
    >
      {initials}
    </span>
  );

  return (
    <>
      <Tooltip
        title={wide ? 'Switch merchant · ⌘M' : `${merchant?.title ?? 'No merchant selected'} · switch (⌘M)`}
        placement="right"
        mouseEnterDelay={0.3}
      >
        <button
          type="button"
          onClick={openPicker}
          aria-label={`Merchant: ${merchant?.title ?? 'none selected'}. Switch merchant`}
          style={{
            margin: wide ? '12px 10px 6px' : '12px 8px 6px',
            padding: wide ? '10px 10px' : 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: wide ? 'flex-start' : 'center',
            gap: 10,
            borderRadius: 12,
            border: `1px solid ${isDark ? colors.slate[700] : colors.ink[100]}`,
            background: isDark ? colors.slate[800] : `linear-gradient(180deg, ${colors.ink[50]}, #FFFFFF)`,
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: 'inherit',
            color: 'inherit',
          }}
        >
          {avatar}
          {wide && (
            <>
              <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: isDark ? colors.slate[400] : colors.slate[500] }}>
                  Merchant
                </span>
                {showSkeleton ? (
                  <Skeleton.Input active size="small" style={{ width: 96, height: 16, marginTop: 3 }} />
                ) : (
                  <span
                    style={{
                      fontSize: 13.5,
                      fontWeight: 650,
                      color: isDark ? colors.slate[50] : colors.slate[900],
                      // Two lines before truncating: merchant names are long and this is the one place they must be read.
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                    }}
                  >
                    {title}
                  </span>
                )}
              </span>
              <SwapOutlined style={{ fontSize: 13, color: colors.ink[600], flexShrink: 0 }} />
            </>
          )}
        </button>
      </Tooltip>
      <MerchantPickerDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
});

function initialsOf(title: string): string {
  const words = title.split(/\s+/).filter((w) => /[A-Za-z0-9]/.test(w));
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || '?';
}
